import { AccountingPackageNameId } from '../Models/Api/AccountingPackage';
import {
    IDelegatedAccessToken
} from '../Models/Api/ClientBase';
import { AccountingPackage } from '../Models/Api/strongbox.financialportal';

import {
    ConnectionsClient,
    FinancialRecordsClient,
} from '../Models/Api/strongbox.client';

import {
    AccountingImportOptions,
    ConnectionDescriptor,
    ConnectionRequest,
    ConnectionRequestDescriptor,
    ConnectionRequestErrorCode,
    ConnectionRequestParameters,
    ConsumerMetadata,
    FinancialImportParameters,
    FinancialStatementImportOptions,
    IFinancialStatementImportOptions,
    IReceivablesAndPayablesOptions,
    ITransactionImportOptions,
    PrivacyControl,
    ReceivablesAndPayablesOptions,
    TransactionImportOptions,
} from '../Models/Api/strongbox.models';

// Number of milliseconds before checking to see if window is closed
const watchAuthWindowTimeout = 500;

/**
 * ImportDayMonthYear is used to provide the last day of a financial import. The date provided should not be greater 
 * than the current date. If it is not provided, the current date is used.
 * 
 * @member {number } month - is required if mostRecentDate is provided and represents the 0 based month, 
 * i.e. January = 0 and December = 11.
 * @member {number} day is required if mostRecentDate is provided.  For convenience, if you simply want to represent 
 * the end of a month, you can pass 0 for the day. For example, if you want to collect information through the end 
 * of April 2021, you can pass 3 for the month (months are zero based recall), 0 for the day and 2021 for the year. 
 * Otherwise, this is the 1 based day of the month representing the last full day of imported information. 
 * @member {number} year - is required if mostRecentDate is provided and is simply the full year, e.g. 2021.
 * 
 * */

export type ImportDayMonthYear = {
    month: number;
    day: number;
    year: number;
}

/*
 * Options for passing to strongbox import job.
 *
 * month and year values are based on javascript Date standards, that is month is 0 based.
 */

export type LenderConnectionOptions = {
    mostRecentDate?: ImportDayMonthYear;
    financialStatementsPeriod?: IFinancialStatementImportOptions;
    transactionsPeriod?: ITransactionImportOptions;
    payablesPeriod?: IReceivablesAndPayablesOptions;
    receivablesPeriod?: IReceivablesAndPayablesOptions;
    anonymizeCustomersAndVendors: boolean;
    provideUserCopy: boolean;
};

/*
* What UI experience is the user coming in through.  borrower and lender are pretty well defined.
* Widget is a corner case at this point. There are no active customers using the widget. At the
* moment, the 'borrower' flow is specifically handled differently so lender and widget will be the
* same.  This can be addressed in the future if necessary.
*/

export type PortalFlowType = 'borrower' | 'lender' | 'widget';

/*
 * StrongboxConnectionDescriptor is used for containing parameters used in the three phases of starting a financial import
 * These are:
 * 
 * 1. Finding an existing connection for an accounting package.
 * 
 * 2. Get a connection request descriptor including an id for the connection.  This will include the URL that can be
 * invoked to connect with the accounting package.  This is done prior to actually trying to load and watch the
 * connection window so that the URL can be loaded directly in the onClick handler of the button which will invoke
 * the window.   This helps with popup blocking.
 * 
 * 3. Starting the actual import itself.
 */

export type StrongboxConnectionDescriptor = {
    accountingPackage: AccountingPackage,
    delegatedAccessToken: IDelegatedAccessToken,
    strongboxUri: string,
    orgId: string,
    existingConnectionId?: string,
    submissionId?: string,
    lenderManagedOptions?: LenderConnectionOptions,
    initiator?: string,
    sourceFlow: PortalFlowType,
};

export type ConnectUrlParameter = {
    key: string;
    value: string;
}

/*
 * StrongboxConnectionRequest is used for containing parameters used in connecting an accounting system.  These
 * parameters are used for the following:
 *
 * 1. Finding an existing connection for an accounting package.
 *
 * 2. Get a connection request descriptor including an id for the connection.  This will include the URL that can be
 * invoked to connect with the accounting package.  This is done prior to actually trying to load and watch the
 * connection window so that the URL can be loaded directly in the onClick handler of the button which will invoke
 * the window.   This helps with popup blocking.
 */

export type StrongboxConnectionRequest = {
    accountingPackage: AccountingPackage,
    delegatedAccessToken: IDelegatedAccessToken,
    strongboxUri: string,
    orgId: string,
    existingConnectionId?: string,
    submissionId?: string,
    sourceFlow: PortalFlowType,
    connectParameters?: ConnectUrlParameter[],
};

export type StrongboxImportRequest = {
    accountingPackage: AccountingPackageNameId,
    lenderManagedOptions?: LenderConnectionOptions,
    initiator?: string,
    sourceFlow: PortalFlowType,
    delegatedAccessToken: IDelegatedAccessToken,
    orgId: string,
    strongboxUri: string,
    submissionId?: string,
    connectionId: string,
    initialMetadata?: ConsumerMetadata[];
};

export const AppendMetadataToConnectionEndpoint = (connectorEndpoint: string, connectParameters?: ConnectUrlParameter[]): string => {
    let endpoint = connectorEndpoint;

    if (!!connectParameters) {
        connectParameters.forEach(p => {
            endpoint += `&${encodeURI(`${p.key}=${p.value}`)}`
        });
    }

    return endpoint || '';
}

export const LoadConnectWindow = (
    cxnRequestDescriptor: ConnectionRequestDescriptor | undefined,
    onOpen?: (authWindow: Window) => void,
    connectParameters?: ConnectUrlParameter[]
): Window | undefined => {
    const height = 970;
    const width = 750;

    let endpoint: string = 'about:blank';

    if (cxnRequestDescriptor && cxnRequestDescriptor.connectionEndpoint) {
        endpoint = AppendMetadataToConnectionEndpoint(cxnRequestDescriptor.connectionEndpoint, connectParameters);
    }

    const windowHandle = window.open(
        endpoint,
        (cxnRequestDescriptor && `${cxnRequestDescriptor.datasourceNameId}_${cxnRequestDescriptor.id}_login_popup`) || '_blank',
        `left=${((document.body.clientWidth) / 2) - (width / 2)}` +
        `,top=${((document.body.clientHeight) / 2) - (height / 2)}` +
        `,width=${width},height=${height}` +
        ',location=yes,toolbar=0,status=0,menubar=0,scrollbars=0'
    );

    if (windowHandle) {
        onOpen && onOpen(windowHandle);
        windowHandle.focus && windowHandle.focus();
    }

    return windowHandle || undefined;
}

const goodResponse = (status: number): boolean => {
    return status === 200 || status === 201 || status === 204;
}

/*
 * Get financial connection descriptor which provides all the information necessary to show a dialog for
 * connecting to a financial package.
 *
 * Returns undefined if something bad happens.  In that case onError is called.
 * 
 * On successful result, a new StrongboxConnectionDescriptor is called containing a valid ConnectionRequestDescriptor
 */

export const StartGetFinancialsConnectionDescriptor = async (
    connectionInfo: StrongboxConnectionDescriptor,
    onError: (msg: string, detailedMsg: string) => void,
): Promise<ConnectionRequestDescriptor | undefined> => {
    const connections = new ConnectionsClient(connectionInfo.delegatedAccessToken, connectionInfo.strongboxUri);

    let parameters: { [key: string]: string; } | undefined = undefined;

    if (connectionInfo.sourceFlow === 'borrower') {
        parameters = {
            mode: 'borrower'
        }
    }

    const requestParameters = new ConnectionRequestParameters({
        datasourceNameId: connectionInfo.accountingPackage.nameId,
        parameters
    });

    try {
        const response = await connections.createRequest(
            connectionInfo.orgId,
            requestParameters,
        );

        if (!goodResponse(response.status)) {
            const detailedError = `Unable to create request for ${connectionInfo.accountingPackage.nameId}, response status: ${response.status.toString()}`;
            onError('An error occurred communicating with the server, please try again later', detailedError);

            return undefined;
        }

        return response.result;

    } catch (e) {
        const detailedError = `Unable to create request for ${connectionInfo.accountingPackage.nameId}, response status: ${e.description || 'Unrecognized response code'}`;
        onError('An error occurred communicating with the server, please try again later', detailedError);

        return undefined;
    }
}

export const GetFinancialsConnectionDescriptor = async (
    connectionInfo: StrongboxConnectionDescriptor,
    onError: (msg: string, detailedMsg: string) => void,
): Promise<ConnectionRequestDescriptor | undefined> => {
    return await StartGetFinancialsConnectionDescriptor(connectionInfo, onError);
}

/*
 * Returns the local date in ISO format.
*/
function toISOLocalDateString(d: Date): string {
    return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
}

/*
 * Shows authorization dialog for the accounting package provided.  There are 3 distinct possible outcomes.
 * For each of the outcomes, 1 and only 1 of the terminal functions (onError, onAborted, onConnected) will be called
 * 
 * 1. Error. Something bad happened, onError is called.
 * 2. User closed the authorization window but didn't authenticate.   onAborted
 * 3. User authorized and the accounting system is connected. onConnected
 * 
 * cxnRequestDescriptor can be undefined in the event that connectionInfo has an existing connection id.
 */

export const ConnectAccountingSystem = async (
    cxnRequest: StrongboxConnectionRequest,
    apiRequestParameters: ConnectionRequestDescriptor | undefined,
    windowHandle: Window | undefined,
    onError: ((msg: string, detailedMsg: string, errorCode?: ConnectionRequestErrorCode) => void) | undefined,
    onConnected: ((cxnRequest: StrongboxConnectionRequest, apiRequestParameters?: ConnectionRequestDescriptor) => void) | undefined,
    onAborted: (() => void) | undefined,
    isCancelled: () => boolean,
    monitorWindowHandleClosed: boolean,
): Promise<void> => {
    try {
        const connections = new ConnectionsClient(cxnRequest.delegatedAccessToken, cxnRequest.strongboxUri);

        let useConnectionId = cxnRequest.existingConnectionId;

        if (!useConnectionId) {
            var success = false;

            if (!(windowHandle && apiRequestParameters)) {
                if (!!onError) {
                    const detailedError = `Unable to connect with ${cxnRequest.accountingPackage.nameId}, response status: required parameter windowHandle or apiRequestParameters is missing`;
                    onError('An error occurred communicating with the server, please try again later', detailedError);
                }

                return;
            }

            var complete = false;
            // Distinguishes "finished because the connection reported a failure" (onError already fired)
            // from "finished because the user closed the popup" (an abort). Without this, an errored poll
            // falls through to the onAborted call below and overwrites the error outcome with a silent
            // abort — the "one and only one terminal callback" contract above would be violated.
            var errored = false;
            var cxnStatus: ConnectionRequest | undefined = undefined;

            do {
                const windowClosedStatus = ((!monitorWindowHandleClosed) || ((windowHandle.closed) || (windowHandle.opener && windowHandle.opener.closed)));

                const cxnResponse = await connections.getRequest(cxnRequest.orgId, apiRequestParameters.id);
                if (cxnResponse) {
                    cxnStatus = cxnResponse.result;

                    if ((cxnStatus) && (cxnStatus.status === "Success")) {
                        success = true;
                    } else if ((cxnStatus) && ((cxnStatus.status === "Error") || (!!cxnStatus.errorCode && cxnStatus.errorCode !== 'None'))) {
                        // A genuine failure: an "Error" status, or a real (non-None) error code even if the
                        // status hasn't flipped to "Error". Surface it via onError with the server's
                        // description, and mark errored so we do NOT also call onAborted below.
                        onError && onError('Unable to connect', cxnStatus.errorDescription || '', cxnStatus.errorCode);
                        errored = true;
                        complete = true;
                    } else if (windowClosedStatus) {
                        complete = true;
                    } else {
                        await new Promise(r => setTimeout(r, watchAuthWindowTimeout));
                    }
                } else {
                    await new Promise(r => setTimeout(r, watchAuthWindowTimeout));
                }
            } while (!complete && !success && !isCancelled());

            if (success) {
                useConnectionId = (cxnStatus && cxnStatus.connectionId) || '';
            } else {
                // Only a window-close-without-authenticating is an abort. If onError already fired for a
                // reported failure, don't also fire onAborted (it would overwrite the error outcome).
                if (!errored) {
                    onAborted && onAborted();
                }
                return;
            }
        }

        onConnected && onConnected(
            {
                ...cxnRequest,
                existingConnectionId: useConnectionId
            },
            apiRequestParameters
        );
    } catch (connectException) {
        onError && onError('An error occurred communicating with the server, please try again later', connectException);
    }
}

export const ImportFinancials = async (
    importRequest: StrongboxImportRequest,
    onError: ((msg: string, detailedMsg: string) => void) | undefined,
    onImportStarted: ((financialRecordId: string, importRequest: StrongboxImportRequest) => void) | undefined,
): Promise<void> => {
    try {
        const financials = new FinancialRecordsClient(importRequest.delegatedAccessToken, importRequest.strongboxUri);

        let reportingDate: string;

        if (importRequest.lenderManagedOptions && importRequest.lenderManagedOptions.mostRecentDate) {
            let month = importRequest.lenderManagedOptions.mostRecentDate.month;
            let year = importRequest.lenderManagedOptions.mostRecentDate.year;
            // See the big comment where mostRecentDate is defined about it's actual use.  It is 1 based
            // but may be 0 to just get the full month passed in month.
            let day = importRequest.lenderManagedOptions.mostRecentDate.day;

            // Months are 0 based in javascript and also in the lenderManagedOptions.  
            // When you pass 0 for the day, it will get you the end
            // of the preceding month.  So, if our month is 0 (January), passing in 1 will
            // result in the date being the end of January for that year.  It is smart enough
            // to deal with an increase in date, so passing in 12 for the month will get
            // december 31 of the current year, i.e. if our month is 11 (december), passing in
            // 2021, 12, 0 will result in 2021-12-31.   

            if (day <= 0) {
                reportingDate = toISOLocalDateString(new Date(year, month + 1, 0));
            } else {
                reportingDate = toISOLocalDateString(new Date(year, month, day));
            }
        } else {
            reportingDate = toISOLocalDateString(new Date());
        }

        let metadata: ConsumerMetadata[] | undefined = importRequest.initialMetadata;

        if (importRequest.submissionId) {
            // could still be undefined.
            if (!metadata) {
                metadata = [];
            }
            metadata.push(new ConsumerMetadata({
                label: 'SubmissionId',
                value: importRequest.submissionId,
            }));
        }

        if (importRequest.initiator) {
            // could still be undefined.
            if (!metadata) {
                metadata = [];
            }
            metadata.push(new ConsumerMetadata({
                label: 'Initiator',
                value: importRequest.initiator,
            }));
        }

        const parameters: FinancialImportParameters = new FinancialImportParameters({
            accountingConnectionId: importRequest.connectionId,
            reportingEndDate: reportingDate,
            consumerMetadata: metadata,
        });

        if (importRequest.lenderManagedOptions) {
            let privacyControls: PrivacyControl[] = [];

            if (importRequest.lenderManagedOptions.anonymizeCustomersAndVendors) {
                privacyControls = ["AnonymizeContactLists", "RedactTransactionMemos"];
            }

            const {
                financialStatementsPeriod,
                transactionsPeriod,
                receivablesPeriod,
                payablesPeriod,
            } = importRequest.lenderManagedOptions;

            // At the strongbox level, numberOfPeriods includes year to date and month to date, so
            // if you want YTD plus 1 extra FY, you would pass 2.  If you pass 0, it turns that 
            // particular piece off.

            const financialStatementOptions = new FinancialStatementImportOptions(
                financialStatementsPeriod ? {
                    reportingPeriod: financialStatementsPeriod.reportingPeriod,
                    numberOfPeriods: financialStatementsPeriod.numberOfPeriods,
                    basisOfAccountingPreference: financialStatementsPeriod.basisOfAccountingPreference,
                } : {
                    reportingPeriod: undefined,
                    numberOfPeriods: 0,
                    basisOfAccountingPreference: undefined,
                }
            );
            const transactionOptions = new TransactionImportOptions(
                transactionsPeriod ? {
                    reportingPeriod: transactionsPeriod.reportingPeriod,
                    numberOfPeriods: transactionsPeriod.numberOfPeriods,
                    basisOfAccountingPreference: transactionsPeriod.basisOfAccountingPreference,
                } : {
                    reportingPeriod: undefined,
                    numberOfPeriods: 0,
                    basisOfAccountingPreference: undefined,
                }
            );
            const receivablesOptions = new ReceivablesAndPayablesOptions(
                receivablesPeriod ? {
                    reportingPeriod: receivablesPeriod.reportingPeriod,
                    numberOfPeriods: receivablesPeriod.numberOfPeriods,
                } : {
                    reportingPeriod: undefined,
                    numberOfPeriods: 0,
                }
            );
            const payablesOptions = new ReceivablesAndPayablesOptions(
                payablesPeriod ? {
                    reportingPeriod: payablesPeriod.reportingPeriod,
                    numberOfPeriods: payablesPeriod.numberOfPeriods,
                } : {
                    reportingPeriod: undefined,
                    numberOfPeriods: 0,
                }
            );

            const importOptions = new AccountingImportOptions({
                privacyControls: privacyControls,
                financialStatements: financialStatementOptions,
                transactions: transactionOptions,
                receivables: receivablesOptions,
                payables: payablesOptions,
            })

            parameters.accountingDataImportOptions = importOptions;
        }

        const importResponse = await financials.import(importRequest.orgId.toLowerCase(), parameters);

        if (!goodResponse(importResponse.status)) {
            if (!!onError) {
                const detailedError = `Failed creating a job to import financial records for ${importRequest.accountingPackage}`;
                onError('A failure occurred communicating with the server. Please try again later', detailedError);
            }

            return;
        }

        const importStatus = importResponse.result;

        onImportStarted && onImportStarted(importStatus.financialRecordId, importRequest);
    } catch (connectException) {
        if (!!connectException.parameter) {
            const detailedError = `Failed creating a job to import financial records for ${importRequest.accountingPackage}`;
            onError && onError(detailedError, connectException.parameter)
        } else {
            onError && onError('A failure occurred communicating with the server. Please try again later', connectException);
        }
    }
}

export const FindConnection = async (accessToken: IDelegatedAccessToken, strongboxUri: string, orgId: string, accountingPackage: AccountingPackageNameId): Promise<ConnectionDescriptor | undefined> => {

    // the purpose of the delegated access token determines whether we have permission to list connections or not. if we don't have permission, don't attempt the request.
    if (!accessToken.purpose.some(p => p === 'ConnectionManagement')) return undefined;

    const connections = new ConnectionsClient(accessToken, strongboxUri);

    try {
        const connectionsResponse = await connections.list(orgId);

        if (!goodResponse(connectionsResponse.status)) {
            return undefined;
        }

        const packageKey = accountingPackage.toLowerCase();

        const connectionsList = connectionsResponse.result;

        const cxnsForOrg = connectionsList.connections.filter(cxn => cxn.datasourceNameId.toLowerCase() === packageKey);

        if (cxnsForOrg) {
            // The iterator functions on arrays don't like async parameters so for loop it is.
            for (var iCxn = 0; iCxn < cxnsForOrg.length; iCxn++) {
                const cxn = cxnsForOrg[iCxn];
                try {
                    var testCxn = await connections.get(orgId, cxn.id);
                    if (goodResponse(testCxn.status) && (testCxn.result.state.toLowerCase() === 'connected')) {
                        return cxn;
                    }
                }
                catch (testCxnException) {
                }
            }
        }

        return undefined;
    } catch (listException) {
        return undefined;
    }
}

export const DisconnectConnection = async (connectionInfo: StrongboxConnectionDescriptor): Promise<boolean> => {
    if (!connectionInfo.existingConnectionId) {
        return false;
    }

    const connections = new ConnectionsClient(connectionInfo.delegatedAccessToken, connectionInfo.strongboxUri);

    try {
        var result = await connections.delete(connectionInfo.orgId, connectionInfo.existingConnectionId);

        return goodResponse(result.status);
    } catch (disconnectException) {
        return false;
    }
}
